import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDocumentTitle } from 'usehooks-ts'
import { z } from 'zod'
import { LinkButtonHref } from '~/features/navigation'
import { getConfigValue } from '~/helpers/envHelpers'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import {
  Button,
  Form,
  FormErrorAlert,
  FormFieldInput,
  processFormError,
} from '@hap/components'

import { DISPLAY, ICON, providers } from './-constants'

export const Route = createFileRoute('/_public/login')({
  validateSearch: z.object({
    redirect: z.string().optional(),
  }),
  component: RouteComponent,
})

const loginSchema = z.object({
  email: z.string().email(),
})

function RouteComponent() {
  const trpc = useTRPC()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const mutator = useMutation(trpc.session.sendCode.mutationOptions())
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)

  useDocumentTitle('Log in to Happitu')

  const form = useForm({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(loginSchema),
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    // Handle login logic here
    mutator
      .mutateAsync({ email: values.email.toLowerCase() })
      .then(() => {
        navigate({
          to: '/auth/code',
          search: { email: values.email.toLowerCase(), redirect: search.redirect },
        })
      })
      .catch((error) => processFormError(form.setError, error))
  })

  return (
    <PublicTemplate width={400}>
      <section className="bg-accent grid gap-8 p-8">
        <h1 className="text-center text-xl font-semibold">Log in to Happitu</h1>
        <div className="grid gap-2">
          <Form {...form}>
            <form onSubmit={handleSubmit} className="grid space-y-4">
              <FormErrorAlert />

              <FormFieldInput
                autoFocus
                name="email"
                control={form.control}
                placeholder="Email"
                type="email"
              />

              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={mutator.isPending}
                loadingLabel="Sending email..."
              >
                Log In With Email
              </Button>
            </form>
          </Form>

          <div className="text-accent-foreground relative my-10 flex items-center border-t text-center">
            <span className="bg-accent absolute top-1/2 left-1/2 inline-block -translate-x-1/2 -translate-y-1/2 p-2">
              Or log in with
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {Object.entries(providers).map(([key]) => {
              const match = providers[key]
              if (match?.hide) return null

              const isLoading = loadingProvider === key
              const icon = ICON[key]

              return (
                <LinkButtonHref
                  key={key}
                  href={`${getConfigValue('VITE_AUTH_URL')}/auth/${key}/authorize`}
                  className="flex items-center gap-2"
                  variant="secondary"
                  size="lg"
                  loading={isLoading}
                  loadingLabel={`Connecting to ${DISPLAY[key] || key}...`}
                  disabled={loadingProvider !== null && !isLoading}
                  onClick={(event) => {
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return
                    }
                    setLoadingProvider(key)
                  }}
                >
                  {icon && <i className="h-[20px] w-[20px]">{icon}</i>}
                  {DISPLAY[key] || key}
                </LinkButtonHref>
              )
            })}
          </div>

          <div className="mt-4 hidden justify-center">
            <LinkButtonHref href="/signup" variant="ghost" size="sm">
              New to Happitu? Create an account
            </LinkButtonHref>
          </div>
        </div>
      </section>
    </PublicTemplate>
  )
}
